import type { IntakeMedia } from "@/lib/patientIntakeApi";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { IntakeNavigation } from "./IntakeNavigation";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import type { IntakeData, UploadedMedia } from "@/lib/types";
import { patientIntakeApi } from "@/lib/patientIntakeApi";
import { CATEGORY_LABELS } from "@/lib/specialtyTags";
import { User, FileText, Shield, Plane, Eye, X, Clock, Camera, ImageOff, Link2, ExternalLink } from "lucide-react";
import { scrollToFirstError } from "@/lib/utils";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Link } from "react-router-dom";

interface ReviewConsentStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  onBack: () => void;
}

export default function ReviewConsentStep({
  data,
  onUpdate,
  onSubmit,
  isSubmitting,
  onBack,
}: ReviewConsentStepProps) {
  const { i18n } = useLingui();
  const [consentGiven, setConsentGiven] = useState(data.consentGiven || false);
  const [error, setError] = useState<string>("");
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    title: string;
  } | null>(null);
  const [hydratedMedia, setHydratedMedia] = useState<UploadedMedia[]>([]);
  const [rawMediaItems, setRawMediaItems] = useState<IntakeMedia[]>([]);
  const hasFetchedOnceRef = useRef(false);

  const documentLinks = data.documentReferenceLinks ?? [];

  const getDocumentLinkLabel = (link: { url: string; label?: string }) => {
    const trimmed = link.label?.trim();
    if (trimmed) {
      return trimmed;
    }
    try {
      return new URL(link.url).hostname;
    } catch {
      return link.url;
    }
  };

  const truncateDocumentUrl = (url: string, max = 52) => {
    if (url.length <= max) {
      return url;
    }
    return `${url.slice(0, max - 1)}…`;
  };

  const handleSubmit = () => {
    if (!consentGiven) {
      setError(i18n._(t`You must agree to the terms and conditions to continue`));
      setTimeout(() => {
        scrollToFirstError();
      }, 100);
      return;
    }

    onUpdate({ consentGiven: true });
    onSubmit();
  };

  const handleConsentChange = (checked: boolean) => {
    setConsentGiven(checked);
    onUpdate({ consentGiven: checked });
    setError("");
  };

  // Get image URL from File or UploadedMedia
  const getImageUrl = useCallback((item: File | UploadedMedia): string | null => {
    if (item instanceof File) {
      return URL.createObjectURL(item);
    }
    if ("downloadUrl" in item && item.downloadUrl) {
      return item.downloadUrl;
    }
    return null;
  }, []);

  // Check if item is an image
  const isImageFile = useCallback((item: File | UploadedMedia): boolean => {
    if (item instanceof File) {
      return item.type.startsWith("image/");
    }
    if ("mimeType" in item) {
      return item.mimeType?.startsWith("image/") || false;
    }
    return false;
  }, []);

  // Get filename from File or UploadedMedia
  const getFilename = useCallback((item: File | UploadedMedia): string => {
    if (item instanceof File) {
      return item.name;
    }
    if ("filename" in item) {
      return item.filename;
    }
    return i18n._(t`Unknown`);
  }, [i18n]);

  // Reset fetch flag when procedure changes to re-fetch and re-filter media
  useEffect(() => {
    hasFetchedOnceRef.current = false;
  }, [data.procedures]);

  // Fetch media from backend
  useEffect(() => {
    if (!data.formUid || hasFetchedOnceRef.current) {
      return;
    }

    // Skip if uploads were intentionally cleared (empty object with no keys)
    const uploadsCleared = data.uploads && Object.keys(data.uploads).length === 0;
    if (uploadsCleared) {
      console.log('⏭️ ReviewStep: Skipping media fetch - uploads were cleared');
      return;
    }

    let isCancelled = false;

    const hydrateMediaFromBackend = async () => {
      try {
        setIsLoadingMedia(true);
        const response = await patientIntakeApi.getIntakeStatus(data.formUid!);
        const mediaItems: IntakeMedia[] =
          (response.data?.media ?? []) as IntakeMedia[];

        if (isCancelled || !Array.isArray(mediaItems) || mediaItems.length === 0) {
          return;
        }

        // Determine current procedure type
        const currentProcedure = typeof data.procedures === 'string' 
          ? data.procedures 
          : data.procedures?.slug || '';
        const isDentistry = currentProcedure === 'dentistry' || currentProcedure === 'general-dentistry';

        // Define dentistry-specific photo positions
        const dentistryPhotoPositions = [
          'upper_occlusal', 'frontal_retracted_apart', 'lower_occlusal',
          'right_buccal', 'frontal_in_occlusion', 'left_buccal',
          'front', 'leftProfile', 'rightProfile', 'upperArch', 'lowerArch', 'biteFront'
        ];

        // Filter media based on procedure type
        const filteredMediaItems = mediaItems.filter((media) => {
          const photoPosition = media.photoPosition as string | undefined;
          
          // If no photoPosition, it's a general upload (idPassport, labsImaging, photos)
          if (!photoPosition) {
            return true;
          }

          // If current procedure is dentistry, show dentistry photos
          if (isDentistry) {
            return dentistryPhotoPositions.includes(photoPosition);
          }

          // If current procedure is NOT dentistry, exclude dentistry photos
          return !dentistryPhotoPositions.includes(photoPosition);
        });

        const uploadedMedia: UploadedMedia[] = filteredMediaItems.map((media) => ({
          id: media.id,
          filename: media.originalFilename || media.filename || i18n._(t`Unknown`),
          mimeType: media.mimeType || "application/octet-stream",
          size: media.fileSize || 0,
          downloadUrl: media.downloadUrl ?? "",
          mediaType: media.mediaType || "photo",
          status: media.status,
          isUploaded: true,
        }));

        if (!isCancelled) {
          setHydratedMedia(uploadedMedia);
          setRawMediaItems(filteredMediaItems); // Store filtered raw response
          hasFetchedOnceRef.current = true;
        }
      } catch (error) {
        console.error("Failed to hydrate media from backend:", error);
      } finally {
        if (!isCancelled) {
          setIsLoadingMedia(false);
        }
      }
    };

    void hydrateMediaFromBackend();

    return () => {
      isCancelled = true;
    };
  }, [data.formUid, data.procedures]);

  // Get mimeType from File or UploadedMedia
  const getMimeType = useCallback((item: File | UploadedMedia): string | null => {
    if (item instanceof File) {
      return item.type;
    }
    if ("mimeType" in item) {
      return item.mimeType || null;
    }
    return null;
  }, []);

  // Check if item is a PDF
  const isPdfFile = useCallback((item: File | UploadedMedia): boolean => {
    const mimeType = getMimeType(item);
    return mimeType === "application/pdf";
  }, [getMimeType]);

  // Combine local uploads with hydrated media (deduplicate by media ID)
  const allMediaItems = useMemo(() => {
    const items: Array<{
      url: string;
      title: string;
      category: "idPassport" | "labsImaging" | "photos";
      isImage: boolean;
      isPdf: boolean;
      mimeType: string | null;
      mediaId?: string;
    }> = [];
    const seenMediaIds = new Set<string>();

    // Create a map of media ID to raw media item for category lookup
    const mediaIdToRawItem = new Map<string, IntakeMedia>();
    rawMediaItems.forEach((rawItem) => {
      if (rawItem.id) {
        mediaIdToRawItem.set(rawItem.id, rawItem);
      }
    });

    // First, process hydrated media from backend (source of truth)
    hydratedMedia.forEach((media) => {
      if (media.downloadUrl) {
        // Skip if we've already seen this media ID
        if (media.id && seenMediaIds.has(media.id)) {
          return;
        }
        if (media.id) {
          seenMediaIds.add(media.id);
        }

        // Determine category from photoPosition (stored when registering)
        // photoPosition contains the category: "labsImaging", "photos", or "idPassport"
        let category: "idPassport" | "labsImaging" | "photos" = "photos";
        const rawItem = media.id ? mediaIdToRawItem.get(media.id) : null;
        const photoPosition = rawItem?.photoPosition || rawItem?.category;
        
        if (photoPosition === "labsImaging" || photoPosition === "labs_imaging") {
          category = "labsImaging";
        } else if (photoPosition === "idPassport" || photoPosition === "id_passport") {
          category = "idPassport";
        } else if (photoPosition === "photos") {
          category = "photos";
        }
        
        const mimeType = media.mimeType || null;
        const isImage = mimeType?.startsWith("image/") || false;
        const isPdf = mimeType === "application/pdf";
        
        items.push({
          url: media.downloadUrl,
          title: media.filename,
          category,
          isImage,
          isPdf,
          mimeType,
          mediaId: media.id,
        });
      }
    });

    // Then process local uploads (only File objects that haven't been uploaded yet)
    // Skip UploadedMedia objects since they're already processed from backend
    ["idPassport", "labsImaging", "photos"].forEach((category) => {
      // Check if uploads were cleared (empty object)
      if (!data.uploads || Object.keys(data.uploads).length === 0) {
        return;
      }
      
      const uploads = data.uploads[category as keyof typeof data.uploads] || [];
      uploads.forEach((item) => {
        // Skip UploadedMedia objects that have been uploaded (they're in hydratedMedia)
        if ("isUploaded" in item && item.isUploaded) {
          const uploadedMedia = item as UploadedMedia;
          // If this media ID was already processed from backend, skip it
          if (uploadedMedia.id && seenMediaIds.has(uploadedMedia.id)) {
            return;
          }
        }

        // Get URL - for images use getImageUrl, for PDFs and other files use downloadUrl or create object URL
        let url: string | null = null;
        const mimeType = getMimeType(item);
        const isImage = isImageFile(item);
        const isPdf = isPdfFile(item);
        
        if (isImage) {
          url = getImageUrl(item);
        } else if (item instanceof File) {
          // For non-image files, create object URL
          url = URL.createObjectURL(item);
        } else if ("downloadUrl" in item && item.downloadUrl) {
          // For uploaded non-image files, use downloadUrl
          url = item.downloadUrl;
        }

        if (url) {
          // Include File objects (not yet uploaded) or UploadedMedia objects 
          // that haven't been processed from hydratedMedia yet
          if (item instanceof File || ("isUploaded" in item && item.isUploaded)) {
            items.push({
              url,
              title: getFilename(item),
              category: category as "idPassport" | "labsImaging" | "photos",
              isImage,
              isPdf,
              mimeType,
            });
          }
        }
      });
    });

    return items;
  }, [data.uploads, hydratedMedia, rawMediaItems, getImageUrl, isImageFile, getFilename, getMimeType, isPdfFile]);

  const openImageModal = useCallback((url: string, title: string) => {
    setSelectedImage({ url, title });
  }, []);

  const closeImageModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  // Image Preview Modal Component
  const ImageModal = () => {
    if (!selectedImage) return null;

    return (
      <div
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        onClick={closeImageModal}
      >
        <div
          className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <h3 className="heading-tertiary text-gray-900">
              {selectedImage.title}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={closeImageModal}
              className="rounded-xl"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="p-6">
            <div className="bg-gray-100 rounded-xl overflow-hidden">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[70vh] object-contain mx-auto"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Image Grid Component
  const ImageGrid = ({
    items,
    categoryLabel,
  }: {
    items: typeof allMediaItems;
    categoryLabel: string;
  }) => {
    if (items.length === 0) return null;

    return (
      <div className="space-y-4">
        <h4 className="body-base font-medium text-gray-700">{categoryLabel}</h4>
        <div className="grid grid-cols-2 gap-4">
          {items.map((item, index) => (
            <Card
              key={`${item.category}-${index}`}
              className="group cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-[#166B93]/30 rounded-xl"
              onClick={() => {
                if (item.isPdf) {
                  // Open PDF in new tab
                  window.open(item.url, '_blank');
                } else {
                  openImageModal(item.url, item.title);
                }
              }}
            >
              <CardContent className="p-3">
                <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden mb-2 relative flex items-center justify-center">
                  {item.isImage ? (
                    <>
                      <img
                        src={item.url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-white/90 hover:bg-white rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col items-center justify-center p-4 text-center">
                        <FileText className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-xs text-gray-500 font-medium">
                          {item.isPdf ? 'PDF' : 'Document'}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-white/90 hover:bg-white rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
                <p className="text-xs text-center font-medium text-gray-700 leading-tight truncate">
                  {item.title}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // Mobile Layout
  const MobileLayout = () => (
    <div className="lg:hidden space-y-6 px-4 sm:px-6 pt-4">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="w-16 h-16 bg-[#166B93]/10 rounded-full flex items-center justify-center mx-auto">
          <FileText className="w-8 h-8 text-[#166B93]" />
        </div>
        <div>
          <div className="flex justify-between items-start">
             <div>
              <h2 className="heading-secondary text-gray-900 mb-1">
                <Trans>Review Your Information</Trans>
              </h2>
              <p className="body-base text-gray-600">
                <Trans>Please review your details before submitting</Trans>
              </p>
             </div>

          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="pb-8 border-b border-gray-100 last:border-0 space-y-6">
        <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-blue-600" />
          </div>
          <h3 className="font-heading font-semibold text-gray-900">
              <Trans>Personal Information</Trans>
          </h3>
        </div>

        <div className="space-y-4">
          <div>
            <span className="body-small text-gray-500 block mb-1">
              <Trans>Full Name</Trans>
            </span>
            <p className="body-base font-medium text-gray-900">
              {data.firstName} {data.lastName}
            </p>
          </div>
          <div>
            <span className="body-small text-gray-500 block mb-1">
              <Trans>Email Address</Trans>
            </span>
            <p className="body-base font-medium text-gray-900 break-all">
              {data.email}
            </p>
          </div>
          <div>
            <span className="body-small text-gray-500 block mb-1">
              <Trans>Phone Number</Trans>
            </span>
            <p className="body-base font-medium text-gray-900">
              {data.phone}
            </p>
          </div>
          {data.country && (
            <div>
              <span className="body-small text-gray-500 block mb-1">
                <Trans>Country</Trans>
              </span>
              <p className="body-base font-medium text-gray-900">
                {data.country}
              </p>
            </div>
          )}
          {data.preferredLanguage && (
            <div>
              <span className="body-small text-gray-500 block mb-1">
                <Trans>Preferred Language</Trans>
              </span>
              <p className="body-base font-medium text-gray-900">
                {data.preferredLanguage === "en" ? i18n._(t`English`) : i18n._(t`Spanish`)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Medical Information */}
      {(data.procedures ||
        data.dateOfBirth ||
        data.preExistingConditions ||
        data.currentMedications ||
        data.allergies ||
        data.majorSurgeries) && (
        <div className="pb-8 border-b border-gray-100 last:border-0 space-y-6">
          <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <FileText className="w-4 h-4 text-red-600" />
            </div>
            <h3 className="font-heading font-semibold text-gray-900">
              <Trans>Medical Information</Trans>
            </h3>
          </div>

          <div className="space-y-4">
            {data.procedures && (
              <div>
                <span className="body-small text-gray-500 block mb-1">
                  <Trans>Procedure</Trans>
                </span>
                <p className="body-base font-medium text-gray-900">
                  {typeof data.procedures === "object" && data.procedures
                    ? data.procedures.name
                    : (data.procedures && CATEGORY_LABELS[data.procedures as string]) || data.procedures || i18n._(t`Not provided`)}
                </p>
              </div>
            )}
            {/* Merged Allergies and Medications Display for Mobile */}
            {data.currentMedications && data.allergies && data.currentMedications === data.allergies ? (
               <div>
                 <span className="body-small text-gray-500 block mb-1">
                   <Trans>Allergies and Medications</Trans>
                 </span>
                 <p className="body-base font-medium text-gray-900 whitespace-pre-line">
                   {data.allergies}
                 </p>
               </div>
            ) : (
                <>
                {data.currentMedications && (
                  <div>
                    <span className="body-small text-gray-500 block mb-1">
                      <Trans>Current Medications</Trans>
                    </span>
                    <p className="body-base font-medium text-gray-900">
                      {data.currentMedications}
                    </p>
                  </div>
                )}
                {data.allergies && (
                  <div>
                    <span className="body-small text-gray-500 block mb-1">
                      <Trans>Allergies</Trans>
                    </span>
                    <p className="body-base font-medium text-gray-900">
                       {/* Strip redundant prefix if present */}
                      {data.allergies.replace(/^Allergies:\s*/i, '')}
                    </p>
                  </div>
                )}
                </>
            )}
            {data.majorSurgeries && (
              <div>
                <span className="body-small text-gray-500 block mb-1">
                  <Trans>Previous Surgeries</Trans>
                </span>
                <p className="body-base font-medium text-gray-900">
                  {data.majorSurgeries}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Travel Information */}
      {(data.plannedTravelMonth || data.travelingWith) && (
        <div className="pb-8 border-b border-gray-100 last:border-0 space-y-6">
          <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Plane className="w-4 h-4 text-green-600" />
            </div>
            <h3 className="font-heading font-semibold text-gray-900">
              <Trans>Travel Information</Trans>
            </h3>
          </div>

          <div className="space-y-4">
            {data.plannedTravelMonth && (
              <div>
                <span className="body-small text-gray-500 block mb-1">
                  <Trans>Planned Travel Month</Trans>
                </span>
                <p className="body-base font-medium text-gray-900">
                  {data.plannedTravelMonth}
                </p>
              </div>
            )}
            {data.travelingWith && (
              <div>
                <span className="body-small text-gray-500 block mb-1">
                  <Trans>Traveling With</Trans>
                </span>
                <p className="body-base font-medium text-gray-900">
                  {data.travelingWith}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Uploaded Documents & reference links */}
      {(allMediaItems.length > 0 || documentLinks.length > 0) && (
        <div className="pb-8 border-b border-gray-100 last:border-0 space-y-6">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <Camera className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="font-heading font-semibold text-gray-900">
                <Trans>Uploaded Documents &amp; Photos</Trans>
              </h3>
            </div>
            {(allMediaItems.length > 0 || documentLinks.length > 0) && (
              <div className="flex flex-col items-end gap-1 text-right">
                {allMediaItems.length > 0 && (
                  <span className="body-small font-medium text-green-800">
                    {allMediaItems.length}{" "}
                    {allMediaItems.length === 1 ? i18n._(t`document`) : i18n._(t`documents`)}
                  </span>
                )}
                {documentLinks.length > 0 && (
                  <span className="body-small font-medium text-blue-900">
                    {documentLinks.length}{" "}
                    {documentLinks.length === 1 ? i18n._(t`link`) : i18n._(t`links`)}
                  </span>
                )}
              </div>
            )}
          </div>

                {isLoadingMedia && (
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    <Clock className="w-4 h-4" />
                    <span>
                      <Trans>Loading documents...</Trans>
                    </span>
                  </div>
                )}

          {allMediaItems.length > 0 && (
            <div className="space-y-6">
              {allMediaItems.filter((item) => item.category === "idPassport").length > 0 && (
                <ImageGrid
                  items={allMediaItems.filter((item) => item.category === "idPassport")}
                  categoryLabel={i18n._(t`ID/Passport`)}
                />
              )}
              {allMediaItems.filter((item) => item.category === "labsImaging").length > 0 && (
                <ImageGrid
                  items={allMediaItems.filter((item) => item.category === "labsImaging")}
                  categoryLabel={i18n._(t`Lab Results & Imaging`)}
                />
              )}
              {allMediaItems.filter((item) => item.category === "photos").length > 0 && (
                <ImageGrid
                  items={allMediaItems.filter((item) => item.category === "photos")}
                  categoryLabel={i18n._(t`Photos`)}
                />
              )}
            </div>
          )}

          {documentLinks.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50">
                  <Link2 className="h-4 w-4 text-[#166B93]" />
                </div>
                <h4 className="font-heading text-sm font-semibold text-gray-900">
                  <Trans>Document links</Trans>
                </h4>
              </div>
              <ul className="space-y-3">
                {documentLinks.map((link, index) => (
                  <li
                    key={`${link.url}-${index}`}
                    className="flex flex-col gap-2 rounded-xl border border-gray-100 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-start gap-3">
                      <Link2 className="mt-0.5 h-4 w-4 text-[#166B93]" />
                      <div className="space-y-1">
                        <p className="body-base font-medium text-gray-900">
                          {getDocumentLinkLabel(link)}
                        </p>
                        <p className="body-small break-all text-gray-500">
                          {truncateDocumentUrl(link.url)}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="sm:self-center" asChild>
                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        <Trans>Open</Trans>
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Privacy and Consent */}
      <div className="pb-8 border-b border-gray-100 last:border-0 space-y-6">
        <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <Shield className="w-4 h-4 text-green-600" />
          </div>
          <h3 className="font-heading font-semibold text-gray-900">
            <Trans>Privacy &amp; Consent</Trans>
          </h3>
        </div>

        <div className="bg-blue-50 rounded-xl p-4 space-y-3">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="consent"
              checked={consentGiven}
              onCheckedChange={handleConsentChange}
              className="mt-1"
            />
            <div className="flex-1">
              <label
                htmlFor="consent"
                className="body-base font-medium text-blue-900 cursor-pointer block mb-2"
              >
                <Trans>I consent to securely sharing my information for medical consultation</Trans>
              </label>
              <p className="body-small text-blue-700">
                <Trans>
                  Your information will be encrypted, stored securely, and only used for your medical consultation. You can request deletion at any time.
                </Trans>
              </p>
              {error && (
                <p className="text-red-500 text-xs mt-2">{error}</p>
              )}
            </div>
          </div>

          <div className="text-xs text-blue-600 space-y-1 pt-2 border-t border-blue-200">
            <p>
              <Link
                to="privacy-policy"
                target="_blank"
                className="underline hover:text-blue-800"
              >
                <Trans>Privacy Policy</Trans>
              </Link>{" "}
              |
              <Link
                to="privacy-policy"
                target="_blank"
                className="underline hover:text-blue-800 ml-1"
              >
                <Trans>Terms of Service</Trans>
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="space-y-3">
        <IntakeNavigation
             onBack={onBack}
             onNext={handleSubmit}
             isNextLoading={isSubmitting}
             isNextDisabled={!consentGiven}
             nextLabel={<Trans>Submit &amp; Continue to Verification</Trans>}
             className="border-none mt-0 pt-0"
        />

        {!consentGiven && (
          <p className="text-xs text-gray-500 text-center">
            <Trans>Please provide consent to continue</Trans>
          </p>
        )}
      </div>

      {/* What Happens Next */}
      <div className="bg-gradient-to-r from-[#166B93]/5 to-[#FF9142]/5 rounded-2xl p-6">
        <h4 className="font-heading font-semibold text-gray-900 mb-3">
          <Trans>What happens next?</Trans>
        </h4>
        <ul className="space-y-2">
          <li className="flex items-start gap-3">
            <div className="w-2 h-2 bg-[#166B93] rounded-full mt-2 flex-shrink-0"></div>
            <span className="body-small text-gray-700">
              <Trans>Our medical team will review your information and documents</Trans>
            </span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-2 h-2 bg-[#CEF12F] rounded-full mt-2 flex-shrink-0"></div>
            <span className="body-small text-gray-700">
              <Trans>
                You'll receive a personalized treatment plan and quote within 24-48 business hours
              </Trans>
            </span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-2 h-2 bg-[#FF9142] rounded-full mt-2 flex-shrink-0"></div>
            <span className="body-small text-gray-700">
              <Trans>Schedule a free consultation to discuss your options</Trans>
            </span>
          </li>
        </ul>
      </div>
    </div>
  );

  // Desktop Layout
  const DesktopLayout = () => (
    <div className="hidden lg:block max-w-4xl mx-auto">
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <h2 className="heading-primary text-gray-900">
            <Trans>Review Your Information</Trans>
          </h2>
          <p className="heading-tertiary text-gray-600">
            <Trans>Please review your details before submitting</Trans>
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Left Column - Personal & Medical Info */}
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-2xl border border-gray-100 p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="heading-tertiary text-gray-900">
                  <Trans>Personal Information</Trans>
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="body-base text-gray-500">
                    <Trans>Full Name</Trans>
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.firstName} {data.lastName}
                  </p>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="body-base text-gray-500">
                    <Trans>Email Address</Trans>
                  </span>
                  <p className="body-base font-medium text-gray-900 break-all">
                    {data.email}
                  </p>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="body-base text-gray-500">
                    <Trans>Phone Number</Trans>
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.phone}
                  </p>
                </div>
                {data.country && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="body-base text-gray-500">
                      <Trans>Country</Trans>
                    </span>
                    <p className="body-base font-medium text-gray-900">
                      {data.country}
                    </p>
                  </div>
                )}
                {data.preferredLanguage && (
                  <div className="flex justify-between items-center py-3">
                    <span className="body-base text-gray-500">
                      <Trans>Preferred Language</Trans>
                    </span>
                    <p className="body-base font-medium text-gray-900">
                      {data.preferredLanguage === "en" ? i18n._(t`English`) : i18n._(t`Spanish`)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Medical Information */}
            {(data.procedures ||
              data.dateOfBirth ||
              data.preExistingConditions ||
              data.currentMedications ||
              data.allergies ||
              data.majorSurgeries) && (
              <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="heading-tertiary text-gray-900">
                    <Trans>Medical Information</Trans>
                  </h3>
                </div>

                <div className="space-y-4">
                  {data.procedures && (
                    <div className="flex justify-between items-start py-3 border-b border-gray-100">
                      <span className="body-base text-gray-500">
                        <Trans>Procedure</Trans>
                      </span>
                      <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                        {typeof data.procedures === "object" && data.procedures
                          ? data.procedures.name
                          : (data.procedures && CATEGORY_LABELS[data.procedures as string]) || data.procedures || i18n._(t`Not provided`)}
                      </p>
                    </div>
                  )}
                  {data.dateOfBirth && (
                    <div className="flex justify-between items-start py-3 border-b border-gray-100">
                      <span className="body-base text-gray-500">
                        <Trans>Date of Birth</Trans>
                      </span>
                      <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                        {data.dateOfBirth}
                      </p>
                    </div>
                  )}
                  {data.preExistingConditions && (
                    <div className="flex justify-between items-start py-3 border-b border-gray-100">
                      <span className="body-base text-gray-500">
                        <Trans>Pre-existing Conditions</Trans>
                      </span>
                      <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                        {data.preExistingConditions}
                      </p>
                    </div>
                  )}
                  {/* Merged Allergies and Medications Display for Desktop */}
                  {data.currentMedications && data.allergies && data.currentMedications === data.allergies ? (
                     <div className="flex justify-between items-start py-3 border-b border-gray-100">
                       <span className="body-base text-gray-500">
                         <Trans>Allergies and Medications</Trans>
                       </span>
                       <p className="body-base font-medium text-gray-900 text-right max-w-xs whitespace-pre-line">
                         {data.allergies}
                       </p>
                     </div>
                  ) : (
                      <>
                      {data.currentMedications && (
                        <div className="flex justify-between items-start py-3 border-b border-gray-100">
                          <span className="body-base text-gray-500">
                            <Trans>Current Medications</Trans>
                          </span>
                          <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                            {data.currentMedications}
                          </p>
                        </div>
                      )}
                      {data.allergies && (
                        <div className="flex justify-between items-start py-3 border-b border-gray-100">
                          <span className="body-base text-gray-500">
                            <Trans>Allergies</Trans>
                          </span>
                          <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                             {/* Strip redundant prefix if present */}
                             {data.allergies.replace(/^Allergies:\s*/i, '')}
                          </p>
                        </div>
                      )}
                      </>
                  )}
                  {data.majorSurgeries && (
                    <div className="flex justify-between items-start py-3">
                      <span className="body-base text-gray-500">
                        <Trans>Previous Surgeries</Trans>
                      </span>
                      <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                        {data.majorSurgeries}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Travel Information */}
            {(data.plannedTravelMonth || data.travelingWith) && (
              <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Plane className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="heading-tertiary text-gray-900">
                    <Trans>Travel Information</Trans>
                  </h3>
                </div>

                <div className="space-y-4">
                  {data.plannedTravelMonth && (
                    <div className="flex justify-between items-start py-3 border-b border-gray-100">
                      <span className="body-base text-gray-500">
                        <Trans>Planned Travel Month</Trans>
                      </span>
                      <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                        {data.plannedTravelMonth}
                      </p>
                    </div>
                  )}
                  {data.travelingWith && (
                    <div className="flex justify-between items-start py-3">
                      <span className="body-base text-gray-500">
                        <Trans>Traveling With</Trans>
                      </span>
                      <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                        {data.travelingWith}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Documents */}
          <div className="space-y-6">
            {/* Uploaded Documents */}
            <div className="bg-white rounded-2xl border border-gray-100 p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Camera className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="heading-tertiary text-gray-900">
                    <Trans>Uploaded Documents & Photos</Trans>
                  </h3>
                </div>
                {(allMediaItems.length > 0 || documentLinks.length > 0) && (
                  <div className="flex flex-col items-end gap-1 text-right">
                    {allMediaItems.length > 0 && (
                      <span className="body-base font-medium text-green-800">
                        {allMediaItems.length}{" "}
                        {allMediaItems.length === 1 ? i18n._(t`document`) : i18n._(t`documents`)}
                      </span>
                    )}
                    {documentLinks.length > 0 && (
                      <span className="body-small font-medium text-blue-900">
                        {documentLinks.length}{" "}
                        {documentLinks.length === 1 ? i18n._(t`link`) : i18n._(t`links`)}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {isLoadingMedia && (
                <div className="flex items-center gap-2 text-sm text-blue-600 mb-4">
                  <Clock className="w-4 h-4" />
                  <span>
                    <Trans>Loading documents...</Trans>
                  </span>
                </div>
              )}

              {allMediaItems.length > 0 && (
                <div className="space-y-6">
                  {allMediaItems.filter((item) => item.category === "idPassport").length > 0 && (
                    <ImageGrid
                      items={allMediaItems.filter((item) => item.category === "idPassport")}
                      categoryLabel={i18n._(t`ID/Passport`)}
                    />
                  )}
                  {allMediaItems.filter((item) => item.category === "labsImaging").length > 0 && (
                    <ImageGrid
                      items={allMediaItems.filter((item) => item.category === "labsImaging")}
                      categoryLabel={i18n._(t`Lab Results & Imaging`)}
                    />
                  )}
                  {allMediaItems.filter((item) => item.category === "photos").length > 0 && (
                    <ImageGrid
                      items={allMediaItems.filter((item) => item.category === "photos")}
                      categoryLabel={i18n._(t`Photos`)}
                    />
                  )}
                </div>
              )}

              {documentLinks.length > 0 && (
                <div className={`space-y-4 ${allMediaItems.length > 0 ? "mt-8" : ""}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50">
                      <Link2 className="h-5 w-5 text-[#166B93]" />
                    </div>
                    <h4 className="heading-tertiary text-gray-900">
                      <Trans>Document links</Trans>
                    </h4>
                  </div>
                  <ul className="space-y-3">
                    {documentLinks.map((link, index) => (
                      <li
                        key={`${link.url}-${index}`}
                        className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50/40 p-4 md:flex-row md:items-center md:justify-between"
                      >
                        <div className="flex items-start gap-3">
                          <Link2 className="mt-1 h-5 w-5 text-[#166B93]" />
                          <div className="space-y-1">
                            <p className="body-base font-semibold text-gray-900">
                              {getDocumentLinkLabel(link)}
                            </p>
                            <p className="body-small break-all text-gray-600">
                              {truncateDocumentUrl(link.url)}
                            </p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" className="md:self-center" asChild>
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="mr-2 h-4 w-4" />
                            <Trans>Open</Trans>
                          </a>
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {allMediaItems.length === 0 && documentLinks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-200">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <ImageOff className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="body-large font-medium text-gray-900 mb-2">
                    <Trans>No Documents Uploaded</Trans>
                  </h4>
                  <p className="body-small text-gray-500 max-w-xs mb-6">
                    <Trans>You haven't uploaded any documents or photos. Providing these helps our team give you the most accurate assessment.</Trans>
                  </p>
                  <Button
                    variant="outline"
                    onClick={onBack}
                    className="border-gray-200 hover:bg-gray-50 text-gray-600"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    <Trans>Add Documents</Trans>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section - Consent & Submit */}
        <div className="space-y-6">
          {/* Privacy and Consent */}
          <div className="bg-white rounded-2xl border border-gray-100 p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="heading-tertiary text-gray-900">
                <Trans>Privacy &amp; Consent</Trans>
              </h3>
            </div>

            <div className="bg-blue-50 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <Checkbox
                  id="consent"
                  checked={consentGiven}
                  onCheckedChange={handleConsentChange}
                  className="mt-1.5 scale-125"
                />
                <div className="flex-1">
                  <label
                    htmlFor="consent"
                    className="body-large font-medium text-blue-900 cursor-pointer block mb-3"
                  >
                    <Trans>I consent to securely sharing my information for medical consultation</Trans>
                  </label>
                  <p className="body-base text-blue-700 mb-4">
                    <Trans>
                      Your information will be encrypted, stored securely, and only used for your medical consultation. You can request deletion at any time.
                    </Trans>
                  </p>
                  {error && (
                    <p className="text-red-500 text-sm mt-2">{error}</p>
                  )}
                  <div className="body-small text-blue-600">
                    <a
                      href="https://jemaservices.com.co/privacy-policy/"
                      className="underline hover:text-blue-800 mr-4"
                    >
                      <Trans>Privacy Policy</Trans>
                    </a>
                    <a
                      href="https://jemaservices.com.co/privacy-policy/"
                      className="underline hover:text-blue-800"
                    >
                      <Trans>Terms of Service</Trans>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Section */}
          <div className="text-center space-y-6">
            <IntakeNavigation
                 onBack={onBack}
                 onNext={handleSubmit}
                 isNextLoading={isSubmitting}
                 isNextDisabled={!consentGiven}
                 nextLabel={<Trans>Submit &amp; Continue to Verification</Trans>}
                 className="mt-0 pt-0 border-none"
            />

            {!consentGiven && (
              <p className="body-base text-gray-500">
                <Trans>Please provide consent to continue</Trans>
              </p>
            )}

            {/* What Happens Next */}
            <div className="bg-gradient-to-r from-[#166B93]/5 via-[#CEF12F]/5 to-[#FF9142]/5 rounded-2xl p-8 max-w-3xl mx-auto">
              <h4 className="heading-tertiary text-gray-900 mb-4">
                <Trans>What happens next?</Trans>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#166B93] rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <p className="body-base text-gray-700">
                    <Trans>Our medical team will review your information and documents</Trans>
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#CEF12F] rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-gray-900 font-bold">2</span>
                  </div>
                  <p className="body-base text-gray-700">
                    <Trans>
                      You'll receive a personalized treatment plan and quote within 24-48 business hours
                    </Trans>
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#FF9142] rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <p className="body-base text-gray-700">
                    <Trans>Schedule a free consultation to discuss your options</Trans>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <ImageModal />
      <MobileLayout />
      <DesktopLayout />
    </>
  );
}
